/*
 * Copyright (C) 2018 BarD Software s.r.o
 */

import {Landing} from '../Routes';
import {ERR_AUTH_TIMED_OUT} from '../utils/ErrorMessages';
import {User} from './Auth';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

declare global {
    interface Window {
        firebaseConfig: any;
    }
}

function firebaseId(id: string): string {
    return `firebase:::${id}`;
}

class FirebaseUser implements User {
    constructor(private readonly principal: firebase.User) {}
    public id(): string {
        return firebaseId(this.principal.uid);
    }
    public idToken(): Promise<string> {
        return this.principal.getIdToken();
    }

    public displayName(): string {
        return this.principal.displayName || this.principal.email || this.principal.uid;
    }

    public email(): string | null {
        return this.principal.email;
    }

    public isEmailVerified(): boolean {
        return this.principal.emailVerified;
    }

    public isEmailAuth(): boolean {
        return this.principal.providerData.some((p) => p?.providerId === 'password');
    }

    public sendEmailVerification(redirectUrl: string): Promise<void> {
        return this.principal.sendEmailVerification({
            url: redirectUrl,
        });
    }
}

export function signOut(redirect = true): Promise<void> {
    return firebase.auth().signOut()
        .then(() => {
            if (redirect) {
                Landing.redirect();
            }
        })
        .catch((e) => console.error(e));
}

export function reauthenticateWithEmail(email: string, password: string): Promise<void> {
    const user = firebase.auth().currentUser;
    if (!user) {
        return Promise.reject(new Error('No user signed in'));
    }
    const credential = firebase.auth.EmailAuthProvider.credential(email, password);
    return user.reauthenticateWithCredential(credential).then(() => undefined);
}

export function deleteCurrentUser(): Promise<void> {
    const user = firebase.auth().currentUser;
    if (!user) {
        return Promise.reject(new Error('No user signed in'));
    }
    return user.delete().then(() => Landing.redirect());
}

export function getUser(): User | undefined {
    const user = firebase.auth().currentUser;
    if (!user) {
        return undefined;
    }

    return new FirebaseUser(user!);
}

let app: firebase.app.App | undefined;

export function initFirebase() {
    if (!app) {
        // Initialize Firebase
        app = firebase.initializeApp(window.firebaseConfig);
    }
}

const AUTH_INIT_TIMEOUT_MS = 8_000;

export function init(): Promise<User> {
    initFirebase();
    return new Promise<User>((resolve, reject) => {
        let settled = false;
        let authUnsubscribe: firebase.Unsubscribe = () => {};
        let tokenUnsubscribe: firebase.Unsubscribe | undefined;
        let timeoutId: number;

        // Single exit point: unsubscribe listeners, cancel the timeout, then resolve or reject once.
        const settle = (action: () => void) => {
            if (settled) {
                return;
            }
            settled = true;
            authUnsubscribe();
            tokenUnsubscribe?.();
            clearTimeout(timeoutId);
            action();
        };

        // Step 1: Firebase calls this once the initial auth state is known; null means not signed in.
        authUnsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                settle(() => reject());
                return;
            }
            if (tokenUnsubscribe) {
                return;
            }
            // Step 2: wait until an id token is available before resolving.
            tokenUnsubscribe = firebase.auth().onIdTokenChanged((newUser) => {
                if (!newUser) {
                    return;
                }
                console.debug('idToken has changed');
                newUser.getIdToken().then(() => {
                    settle(() => resolve(new FirebaseUser(newUser)));
                }).catch((e) => settle(() => reject(e)));
            });
        });

        // Step 3 (last resort): reject if auth never completes within the deadline.
        timeoutId = window.setTimeout(() => {
            settle(() => reject(new Error(ERR_AUTH_TIMED_OUT)));
        }, AUTH_INIT_TIMEOUT_MS);
    });
}
